import React, { ReactNode } from 'react';

import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import GoogleMaterialIcon from '@/components/ui/GoogleMaterialIcon';

interface ContentItem {
  label: string;
  value: string | number;
}

interface InfoCardProps {
  title: string;
  icon?: string | ReactNode;  // Support both string (material icon name) and ReactNode
  materialIcon?: string;
  iconClassName?: string;
  iconColor?: string;  // Support legacy iconColor prop
  actionButton?: ReactNode;
  description?: string;  // One line under the title, e.g. who can see this card
  children?: ReactNode;  // Make optional when using content array
  content?: ContentItem[];  // Support content array pattern
  className?: string;
}

/**
 * InfoCard - A styled card component with a header containing an icon, title, and optional action button.
 * Used for displaying information in a consistent format throughout the application.
 * 
 * Supports two usage patterns:
 * 1. Children pattern: Pass content as children
 * 2. Content array pattern: Pass array of {label, value} objects
 * 
 * @param title - Title of the card
 * @param icon - Material icon name (string) or React node for custom icon
 * @param materialIcon - Google Material icon name to use (alternative to icon)
 * @param iconClassName - Classes to apply to the icon
 * @param iconColor - Legacy prop for icon color (maps to iconClassName)
 * @param actionButton - Button or element to place in the header
 * @param children - Content of the card (for children pattern)
 * @param content - Array of {label, value} objects (for content array pattern)
 * @param className - Additional classes to apply to card
 */
const InfoCard: React.FC<InfoCardProps> = ({
  title,
  icon,
  materialIcon,
  iconClassName = "",
  iconColor,
  actionButton,
  description,
  children,
  content,
  className = ''
}) => {
  // Determine which icon to use and its styling
  const iconName = materialIcon || (typeof icon === 'string' ? icon : undefined);
  const iconElement = typeof icon === 'string' ? undefined : icon;
  const finalIconClassName = iconColor || iconClassName || "text-brand-500";

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-x-3 gap-y-2 space-y-0 pb-2">
        {/* The title shrinks and wraps its own words, but never below 10rem, so a short
            action (Edit, Archive) always sits beside it and only a wide one drops under. */}
        <CardTitle className="flex min-w-[10rem] flex-1 basis-0 items-center gap-3 text-base font-semibold">
          {(iconElement || iconName) && (
            <span
              aria-hidden="true"
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-brand-50 dark:bg-brand-500/10"
            >
              {iconElement && iconElement}
              {iconName && <GoogleMaterialIcon name={iconName} className={finalIconClassName} size="md" />}
            </span>
          )}
          <span className="min-w-0">
            <span className="block leading-tight">{title}</span>
            {description && (
              <span className="mt-0.5 block text-xs font-normal text-gray-500 dark:text-gray-400">{description}</span>
            )}
          </span>
        </CardTitle>

        {/* Wraps under the title when it does not fit beside it (a phone with a
            count badge plus a link, say) instead of squeezing the title to nothing. */}
        {actionButton && (
          <div className="ml-auto flex flex-shrink-0 items-center gap-3">
            {actionButton}
          </div>
        )}
      </CardHeader>
      
      <CardContent>
        {/* Render content array if provided */}
        {content && content.length > 0 ? (
          <dl className="space-y-2">
            {content.map((item, index) => (
              <div key={index} className="flex justify-between items-center py-1.5">
                <dt className="text-sm text-gray-600 dark:text-gray-400">
                  {item.label}
                </dt>
                <dd className="text-sm font-medium text-gray-900 dark:text-white">
                  {item.value}
                </dd>
              </div>
            ))}
          </dl>
        ) : (
          /* Render children if no content array */
          children
        )}
      </CardContent>
    </Card>
  );
};

export default InfoCard; 